import {
  IServiceInfo,
  ISignupField,
  ISignupInfo,
  IColor,
  ITwcConfig,
  IAppPermissions,
  IAppPermission,
  ILinePathMapColorInfo,
  IBusStopLayout,
  ICampaignImage,
  ICampaign,
} from "@/domain/entities/serviceInfo/serviceInfo.types";
import { ParseError } from "@/repositories/shared/errors/parseError";
import { ServiceInfo } from "@/domain/entities/serviceInfo";
import { injectable } from "inversify";
import { err, ok, Result } from "neverthrow";
import {
  IServiceInfoParser,
  HttpServiceInfo,
  HttpSignupInfo,
  HttpSignupField,
  HttpTermsInfo,
  HttpTermsTypeCode,
  HttpPasswordStrength,
  HttpTwcConfig,
  HttpColor,
  HttpAppPermissions,
  HttpAppPermission,
  HttpPathColor,
  HttpBusStopLayout,
  HttpCampaignImage,
  HttpCampaign,
} from "../serviceInfoRepository.types";
import {
  BUS_LOCATION_REFRESH_TIME_MILLIS,
  FavoriteLinesViewType,
  LAST_STOP_ARRIVAL_TIME_THRESHOLD_MIN,
  ON_BOARD_LINE_CALLING_TIME_MILLIS,
  RankCode,
  RankCodeMap,
  TermsTypeCode,
} from "@/types";
import { ITermsInfo } from "@/domain/entities/serviceInfo/termsInfo";
import { HttpRankCode } from "@/repositories/user/userRepository.types";
import dayjs from "dayjs";

function parseRankCode(rankCode?: HttpRankCode) {
  switch (rankCode) {
    case HttpRankCode.REG:
      return RankCode.REG;

    case HttpRankCode.BP:
      return RankCode.BP;

    default:
      return RankCode.NONE;
  }
}

function parsePasswordRegex(
  passwordStrength?: HttpPasswordStrength
): string | undefined {
  if (!passwordStrength) return;

  const regexParts: string[] = [];
  regexParts.push(
    `.{${passwordStrength.lengthLowerBound},${passwordStrength.lengthUpperBound}}`
  );

  // if (passwordStrength.mustIncludeSymbol) {
  //   regexParts.push('(?=.*[-!@#$%^&*(),.?":{}\\/<>;`|\\\\~_])');
  // }

  // if (passwordStrength.mustIncludeUpperLower) {
  //   regexParts.push("(?=.*[a-z])(?=.*[A-Z])");
  // }

  // if (passwordStrength.mustIncludeAlphaNumeric) {
  //   regexParts.push("(?=.*\\d)(?=.*[a-zA-Z])");
  // }
  return `^${regexParts.join("")}$`;
}

function parseSignupField(data: HttpSignupField): ISignupField {
  const signupField: ISignupField = {
    key: data.key,
    type: data.type,
    label: data.label,
    placeholder: data.caption,
    inputFields: data.inputField?.split(","),
    isRequired: data.requiredYn,
    optionValueKey: data.valueField,
    optionNameKey: data.displayTextField,
    subOptionsKey: data.datasourceField,
    datasourceApi: data.datasourceApi,
    isCertRequired: data.certRequiredYn,
    authRequiredForEditYn: data.authRequiredForEditYn,
    certInputField: data.certInputField,
    certLabel: data.certLabel,
    certPlaceholder: data.certCaption,
    parentFieldKey: data.parentKey,
    isValid: !data.parentKey,
    passwordRegex: parsePasswordRegex(data.passwordStrength),
    isEditable: data.updatableYn,
    isDirectlyEditable: data.directInputYn,
    isEdited: false,
    passwordMinLength: data.passwordStrength?.lengthLowerBound ?? 6,
    passwordMaxLength: data.passwordStrength?.lengthUpperBound ?? 16,
    excludeUserInfoYn: data.excludeUserInfoYn,
    isAutoFilledForJoin: data.autoFilledJoinYn,
  };

  return signupField;
}

function parseSignupInfo(data: HttpSignupInfo): ISignupInfo {
  const signupInfo: ISignupInfo = {
    signupFirst: data.joinFirst ?? false,
    fields: data.joinFields?.map(parseSignupField) ?? [],
    useRankCode: parseRankCode(data.useRankCode),
    useRankName: RankCodeMap[data?.useRankCode ?? "MUL-NONE"],
  };
  return signupInfo;
}

function parseTermsTypeCode(code: HttpTermsTypeCode): TermsTypeCode {
  switch (code) {
    case HttpTermsTypeCode.PERSON:
      return TermsTypeCode.PERSON;

    case HttpTermsTypeCode.PERSON_FULL:
      return TermsTypeCode.PERSON_FULL;

    case HttpTermsTypeCode.USAGE:
      return TermsTypeCode.USAGE;

    case HttpTermsTypeCode.MARKET:
      return TermsTypeCode.USAGE;

    case HttpTermsTypeCode.LOCATION:
      return TermsTypeCode.LOCATION;

    case HttpTermsTypeCode.PERLOC:
      return TermsTypeCode.PERLOC;
  }
}

function parseTermsList(data: HttpTermsInfo): ITermsInfo {
  const termsInfo: ITermsInfo = {
    url: data.url,
    code: parseTermsTypeCode(data.code),
    title: data.title,
    required: data.required,
    labelPrefix: data.labelPrefix,
    hideCheckboxYn: data.hideCheckboxYn,
    agreeYn: false,
  };

  return termsInfo;
}

function parseTwcConfig(data?: HttpTwcConfig): ITwcConfig {
  const config: ITwcConfig = {
    brandKey: data?.brandKey ?? "",
    scenarioId: data?.scenarioId ?? "",
    channelType: data?.channelType ?? "",
  };
  return config;
}

function parseColor(data?: HttpColor): IColor {
  const color: IColor = {
    primary: data?.primary,
    primaryDark: data?.primaryDark,
    primaryAlpha3: data?.primaryAlpha3,
    primaryAlpha5: data?.primaryAlpha5,
    primaryAlpha10: data?.primaryAlpha10,
    primaryLight: data?.primaryLight,
    secondPrimary: data?.secondPrimary,
    success: data?.success,
    error: data?.error,
    subColor: data?.subColor,
  };
  return color;
}

function parseAppPermission(data: HttpAppPermission): IAppPermission {
  const appPermission = {
    name: data.name,
    description: data.description,
    required: data.required,
  };

  return appPermission;
}

function parseAppPermissions(
  data: HttpAppPermissions | undefined
): IAppPermissions | undefined {
  if (!data) return;

  const appPermissions: IAppPermissions = {
    android: data.android ? data.android.map(parseAppPermission) : undefined,
    ios: data.ios ? data.ios.map(parseAppPermission) : undefined,
  };

  return appPermissions;
}

function parseWhiteListDomains(data?: string[]): string[] {
  const whiteListDomains = data ?? [
    "https://forms.gle",
    "https://docs.google.com",
  ];

  whiteListDomains.push("https://wizdome.co.kr");
  return whiteListDomains;
}

function parsePathColor(pathColor: HttpPathColor): ILinePathMapColorInfo {
  const parsed: ILinePathMapColorInfo = {
    order: pathColor.order,
    color: pathColor.color,
    markerCdn: pathColor.markerCdn,
  };

  return parsed;
}

function parseStationLayout(data: HttpBusStopLayout): IBusStopLayout {
  const busStopLayout: IBusStopLayout = {
    campusName: data.campusName,
    url: data.url,
  };

  return busStopLayout;
}

const defaultPathColors: ILinePathMapColorInfo[] = [
  {
    order: 0,
    color: "#8dcd33",
    markerCdn: `${process.env.VUE_APP_CDN_BASE_URL}/commute/images/by-service/skhynix/line1.svg`,
  },
  {
    order: 1,
    color: "#10bce3",
    markerCdn: `${process.env.VUE_APP_CDN_BASE_URL}/commute/images/by-service/skhynix/line2.svg`,
  },
  {
    order: 2,
    color: "#3371ff",
    markerCdn: `${process.env.VUE_APP_CDN_BASE_URL}/commute/images/by-service/skhynix/line3.svg`,
  },
];

function parseCampaignImage(data: HttpCampaignImage): ICampaignImage {
  return {
    uid: data.uid,
    imageTypeCode: data.imageTypeCode,
    imageTypeCodeName: data.imageTypeCodeName,
    displayOrder: data.displayOrder,
    name: data.name,
    url: data.url,
  };
}

function parseCampaign(data: HttpCampaign): ICampaign {
  return {
    uid: data.uid,
    name: data.name,
    displayOrder: data.displayOrder,
    images: data.images?.map(parseCampaignImage) ?? [],
    topYn: data.topYn,
    loginHtml: data.loginHtml,
    logoutHtml: data.logoutHtml,
    useBeginDtm: dayjs(data.useBeginDtm).format("YYYY-MM-DD HH:mm:ss"),
    useEndDtm: dayjs(data.useEndDtm).format("YYYY-MM-DD HH:mm:ss"),
    bannerIconColor: data.bannerIconColor,
    altText: data.altText,
  };
}

@injectable()
export class ServiceInfoParser implements IServiceInfoParser {
  constructor() {}

  toDomain(data: HttpServiceInfo): Result<IServiceInfo, ParseError> {
    try {
      const serviceInfoData: IServiceInfo = {
        id: data?.uid ?? "",
        name: data?.name ?? "",
        welcomeMessages: data?.contentProps?.pages?.main?.welcomeTexts ?? [],
        favorieLinesViewType:
          data?.contentProps?.pages?.main?.favoriteLinesViewType ===
          FavoriteLinesViewType.LIST
            ? FavoriteLinesViewType.LIST
            : FavoriteLinesViewType.CARD,
        catchPhrases: data?.contentProps?.pages?.mainLogout?.welcomeText ?? [],
        signupInfos: data?.contentProps?.pages?.joins?.map(parseSignupInfo),
        shouldShowBoardLog: data?.contentProps?.pages?.boardLog?.displayYn,
        shouldShowBookLog: data?.contentProps?.pages?.bookLog?.displayYn,
        shouldShowPayLog: data?.contentProps?.pages?.payLog?.displayYn,
        shouldShowLostAndFound:
          data?.contentProps?.pages?.lostAndFound?.displayYn,
        shouldShowRemainBookCount:
          !!data?.contentProps?.pages?.book?.displayRemainBookCountYn,
        shouldShowTurnList:
          data?.contentProps?.pages?.lineDetail?.displayLineTurnListYn,
        shouldShowTurnPath:
          data?.contentProps?.pages?.lineDetail?.displayLineTurnPathYn ?? true,
        isManagerCertRequired: data.managerCertYn,
        isPublicLineApi: data.publicLineApiYn,
        isPublicTalkApi: data.publicTalkApiYn,
        passwordChangeCycleDays: data.passwordValidIntervalDay,
        passwordChangeAlarmIntervalDay:
          data?.passwordChangeAlarmIntervalDay ?? 0,
        passwordNextChangeLimit: data?.passwordNextChangeLimit ?? 0,
        twcConfig: parseTwcConfig(data?.contentProps?.extensions?.twc),
        logoPath: data.contentProps?.style?.logoPath ?? "",
        bookTimeoutIntervalMin: data.bookTimeoutIntervalMin,
        certValidIntervalMin: data.certValidIntervalMin,
        lastBusStopArrivalThresholdMin:
          data.contentProps?.pages?.lineDetail?.displayExtendedBusLocationMin ??
          LAST_STOP_ARRIVAL_TIME_THRESHOLD_MIN,
        bookWaitYn: data.bookWaitYn,
        boardCheckCode: data.boardCheckCode,
        color: parseColor(data.contentProps?.style?.color),
        appPermissions: parseAppPermissions(data.contentProps?.appPermissions),
        termsList:
          data?.contentProps?.pages?.policy?.terms?.map(parseTermsList) ?? [],
        agreeCodes:
          data?.contentProps?.pages?.policy?.agreeCodes?.map(parseTermsList) ??
          [],
        whiteListDomains: parseWhiteListDomains(
          data.contentProps?.whiteListDomains
        ),
        linePathMapInfo: {
          maxLinePathSelectCount: data?.maxLinePathSelectCount ?? 0,
          pathColors:
            data.linePathColors?.map(parsePathColor) ?? defaultPathColors,
        },
        displayBlockDtmYn: !!data.contentProps?.pages?.main?.displayBlockDtmYn,
        maxDailyTalkCreationCount: data.talkProps?.maxDailyCreationCount,
        maxDailyVocCreationCount: data.vocProps?.maxDailyCreationCount,
        maxDailyLostFoundCreationCount:
          data.lostFoundProps?.maxDailyCreationCount,
        busStopLayouts:
          data.contentProps?.pages?.stationLayouts?.map(parseStationLayout),
        verificationGuideImageUrl: data.contentProps?.pages?.verification?.url,
        lostAndFoundGuideUrl: data.contentProps?.pages?.lostAndFound?.url,
        busLocationUpdateIntervalMillis:
          (data.contentProps?.apis?.busLocation
            ?.busLocationUpdateIntervalSecond ?? 0) * 1000 ||
          BUS_LOCATION_REFRESH_TIME_MILLIS, // 0초인 경우에도 프론트엔드 자체 default값(10초)을 사용한다.
        onBoardRequestIntervalMillis:
          (data.contentProps?.apis?.boarding?.onBoardRequestIntervalSecond ??
            0) * 1000 || ON_BOARD_LINE_CALLING_TIME_MILLIS, // 0초인 경우에도 프론트엔드 자체 default값(30초)을 사용한다.
        lineSortOrder: data.contentProps?.pages?.lineDetail?.sortOrder,
        campaigns: data.campaigns?.map(parseCampaign) ?? [],
        csCenterPhone: data.contentProps?.pages?.mainLogout?.csCenterPhone,
        busCompanyListImageUrl: data.contentProps?.pages?.busCompanyList?.url,
        busCompanyUrl: data.contentProps?.pages?.busCompany?.url,
        lineCacheTtlMin: data.contentProps?.apis?.line?.lineCacheTtlMin,
        favoriteLinesSortOrder:
          data.contentProps?.pages?.main?.favoriteLinesSortOrder,
      };
      const serviceInfo = new ServiceInfo(serviceInfoData);
      return ok(serviceInfo);
    } catch (error: any) {
      return err(new ParseError(error.message));
    }
  }
}
